/**
 * Page action executors — scroll, highlight, click, fill form fields.
 */

import type { DomAnalyzer } from '../engine/domAnalyzer'
import { HighlightRenderer } from '../rendering/highlightRenderer'

export class PageActions {
  private analyzer: DomAnalyzer
  private highlightRenderer: HighlightRenderer

  constructor(analyzer: DomAnalyzer, highlightRenderer: HighlightRenderer) {
    this.analyzer = analyzer
    this.highlightRenderer = highlightRenderer
  }

  scrollTo(index: number): { status: string; description: string } {
    const el = this.analyzer.getElementByIndex(index)
    if (!el) return { status: 'error', description: `Element [${index}] not found` }

    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const label = this.describeElement(el)
    console.log(`[PageActions] Scrolled to [${index}] ${label}`)
    return { status: 'scrolled', description: label }
  }

  highlight(index: number, durationMs = 3000): { status: string; description: string } {
    const el = this.analyzer.getElementByIndex(index)
    if (!el) return { status: 'error', description: `Element [${index}] not found` }

    this.highlightRenderer.highlight(el, durationMs)
    const label = this.describeElement(el)
    console.log(`[PageActions] Highlighting [${index}] ${label} for ${durationMs}ms`)
    return { status: 'highlighted', description: label }
  }

  click(index: number): { status: string; description: string } {
    const el = this.analyzer.getElementByIndex(index)
    if (!el) return { status: 'error', description: `Element [${index}] not found` }

    el.click()
    const label = this.describeElement(el)
    console.log(`[PageActions] Clicked [${index}] ${label}`)
    return { status: 'clicked', description: label }
  }

  fill(index: number, value: string): { status: string; description: string } {
    const el = this.analyzer.getElementByIndex(index)
    if (!el) return { status: 'error', description: `Element [${index}] not found` }

    const tag = el.tagName.toLowerCase()
    const type = (el as HTMLInputElement).type?.toLowerCase()

    if (tag === 'select') {
      const select = el as HTMLSelectElement
      const option = Array.from(select.options).find(
        o => o.text.toLowerCase().includes(value.toLowerCase()) ||
             o.value.toLowerCase() === value.toLowerCase()
      )
      if (option) {
        select.value = option.value
        select.dispatchEvent(new Event('change', { bubbles: true }))
      } else {
        return { status: 'error', description: `No option matching "${value}"` }
      }
    } else if (type === 'checkbox' || type === 'radio') {
      const input = el as HTMLInputElement
      input.checked = value === 'true' || value === '1'
      input.dispatchEvent(new Event('change', { bubbles: true }))
    } else {
      // text, email, tel, number, textarea, contenteditable
      const nativeSetter = Object.getOwnPropertyDescriptor(
        tag === 'textarea' ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype,
        'value'
      )?.set

      if (nativeSetter) {
        nativeSetter.call(el, value)
      } else {
        (el as HTMLInputElement).value = value
      }
      el.dispatchEvent(new Event('input', { bubbles: true }))
      el.dispatchEvent(new Event('change', { bubbles: true }))
    }

    const label = this.describeElement(el)
    console.log(`[PageActions] Filled [${index}] ${label} with "${value}"`)
    return { status: 'filled', description: `${label} = "${value}"` }
  }

  private describeElement(el: HTMLElement): string {
    const tag = el.tagName.toLowerCase()
    const text = (el.textContent ?? '').trim().slice(0, 50)
    const label = el.getAttribute('aria-label') || el.getAttribute('placeholder') || text
    return `<${tag}>${label}</${tag}>`
  }
}
